import React, { forwardRef, type ReactNode } from 'react';

import { Indicator } from '@pkg/components/Sections/Profile/Indicator';

import { type ButtonBaseProps, Colors } from '../ButtonBase';

import { Sizes } from './types';

import {
    StyledButton,
    StyledButtonIndicator,
    type StyledButtonProps,
    StyledEndIcon,
    StyledLoadingIcon,
    StyledLoadingWrapper,
    StyledStartIcon,
} from './styles';

export type ButtonProps = ButtonBaseProps & {
    gutterBottom?: boolean; // TODO: Remove this prop if possible?
} & {
    fluid?: StyledButtonProps['$fluid'];
    size?: StyledButtonProps['$size'];
    loading?: boolean;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    target?: string;
};

export const Button = forwardRef(
    (
        {
            children,
            fluid,
            size = Sizes.medium,
            loading,
            startIcon: startIconProp,
            endIcon: endIconProp,
            disabled,
            indicatorValue,
            ...rest
        }: ButtonProps,
        forwardedRef,
    ) => {
        const indicator = Boolean(indicatorValue) && (
            <StyledButtonIndicator>
                <Indicator value={indicatorValue} />
            </StyledButtonIndicator>
        );

        const startIcon = startIconProp && (
            <StyledStartIcon $loading={loading} $size={size}>
                {startIconProp}
            </StyledStartIcon>
        );

        const endIcon = endIconProp && (
            <StyledEndIcon $loading={loading} $size={size}>
                {endIconProp}
            </StyledEndIcon>
        );

        const Content = (
            <>
                {loading ? (
                    <StyledLoadingWrapper>
                        <StyledLoadingIcon
                            aria-label="Loading"
                            aria-hidden="true"
                            color={rest.color === Colors.white ? 'black' : undefined}
                        />
                    </StyledLoadingWrapper>
                ) : null}
                {startIcon}
                {children}
                {endIcon}
                {indicator}
            </>
        );

        return (
            <StyledButton
                ref={forwardedRef}
                disabled={disabled || loading}
                $hasIcon={!!startIcon || !!endIcon}
                $fluid={fluid}
                $size={size}
                $loading={loading}
                $color={rest.color || Colors.primary}
                {...rest}
            >
                {Content}
            </StyledButton>
        );
    },
);

Button.displayName = 'Button';
